import React, { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Clock, TrendingUp, Calendar, Users, ChevronDown, ChevronUp } from 'lucide-react'
import { getTimeSessions, getUsers } from '../../services/staffServices'
import StaffLayout from './components/StaffLayout'

// Schema: staff_id, staff_name, session_date, start_time, end_time,
//         duration_minutes, status

function formatDuration(mins) {
    if (!mins && mins !== 0) return '—'
    const h = Math.floor(mins / 60)
    const m = mins % 60
    return h > 0 ? `${h}h ${m}m` : `${m}m`
}

function formatTime(ts) {
    if (!ts) return '—'
    return new Date(ts).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })
}

function durationBar(mins, maxMins) {
    if (!maxMins) return 0
    return Math.min(100, Math.round((mins / maxMins) * 100))
}

const WEEK_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

export default function StaffHours() {
    const [staffFilter, setStaffFilter] = useState('')
    const [range, setRange] = useState('week') // 'today' | 'week' | 'month' | 'all'
    const [expandedStaff, setExpandedStaff] = useState(null)

    const { data: sessions = [], isLoading } = useQuery({
        queryKey: ['time-sessions', staffFilter],
        queryFn: () => getTimeSessions(staffFilter ? { staff_id: staffFilter } : {}),
    })
    const { data: users = [] } = useQuery({ queryKey: ['users'], queryFn: getUsers })

    // ── Date range filter ──────────────────────────────────────────────────────
    const today = new Date().toISOString().split('T')[0]
    const weekStart = (() => {
        const d = new Date()
        d.setDate(d.getDate() - d.getDay())
        return d.toISOString().split('T')[0]
    })()
    const monthStart = today.slice(0, 7) + '-01'

    const filteredSessions = useMemo(() => {
        return sessions.filter(s => {
            if (range === 'today') return s.session_date === today
            if (range === 'week') return s.session_date >= weekStart
            if (range === 'month') return s.session_date >= monthStart
            return true
        })
    }, [sessions, range, today, weekStart, monthStart])

    // ── Per-staff aggregation ──────────────────────────────────────────────────
    const staffSummary = useMemo(() => {
        const map = {}
        for (const s of filteredSessions) {
            if (!map[s.staff_id]) {
                map[s.staff_id] = {
                    staff_id: s.staff_id,
                    staff_name: s.staff_name,
                    totalMins: 0,
                    sessionCount: 0,
                    sessions: [],
                    daysPresent: new Set(),
                    lastSeen: null,
                }
            }
            map[s.staff_id].totalMins += s.duration_minutes || 0
            map[s.staff_id].sessionCount++
            map[s.staff_id].sessions.push(s)
            map[s.staff_id].daysPresent.add(s.session_date)
            if (!map[s.staff_id].lastSeen || s.session_date > map[s.staff_id].lastSeen) {
                map[s.staff_id].lastSeen = s.session_date
            }
        }
        return Object.values(map)
            .map(v => ({ ...v, daysPresent: v.daysPresent.size }))
            .sort((a, b) => b.totalMins - a.totalMins)
    }, [filteredSessions])

    const maxMins = staffSummary[0]?.totalMins || 0

    // ── Summary stats ──────────────────────────────────────────────────────────
    const totalHours = Math.round(filteredSessions.reduce((s, i) => s + (i.duration_minutes || 0), 0) / 60 * 10) / 10
    const activeTodayCount = new Set(sessions.filter(s => s.session_date === today).map(s => s.staff_id)).size
    const avgHoursPerPerson = staffSummary.length
        ? Math.round(staffSummary.reduce((s, i) => s + i.totalMins, 0) / staffSummary.length / 60 * 10) / 10
        : 0

    // ── Weekly chart data for expanded staff ──────────────────────────────────
    const weeklyData = useMemo(() => {
        if (!expandedStaff) return []
        const staffSessions = sessions.filter(s => s.staff_id === expandedStaff)
        const last7 = Array.from({ length: 7 }, (_, i) => {
            const d = new Date()
            d.setDate(d.getDate() - (6 - i))
            return d.toISOString().split('T')[0]
        })
        return last7.map(date => {
            const daySessions = staffSessions.filter(s => s.session_date === date)
            const mins = daySessions.reduce((s, i) => s + (i.duration_minutes || 0), 0)
            return { date, label: WEEK_DAYS[new Date(date + 'T00:00:00').getDay()], mins, hours: Math.round(mins / 60 * 10) / 10 }
        })
    }, [expandedStaff, sessions])

    const maxWeeklyMins = Math.max(...(weeklyData.map(d => d.mins)), 1)

    return (
        <StaffLayout>
            <div className="space-y-5">

                {/* Header */}
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                        <h1 className="text-xl font-bold text-white flex items-center gap-2">
                            <Clock size={20} className="text-blue-400" />
                            Staff Hours
                        </h1>
                        <p className="text-slate-400 text-sm mt-0.5">Work time tracked from the StaffSync app</p>
                    </div>
                    {/* Range selector */}
                    <div className="flex gap-1.5 bg-slate-900 border border-white/5 rounded-xl p-1">
                        {[
                            { key: 'today', label: 'Today' },
                            { key: 'week', label: 'This Week' },
                            { key: 'month', label: 'This Month' },
                            { key: 'all', label: 'All Time' },
                        ].map(({ key, label }) => (
                            <button
                                key={key}
                                onClick={() => setRange(key)}
                                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${range === key ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white'}`}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Summary stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="bg-slate-900 rounded-xl border border-white/5 p-4">
                        <div className="flex items-center gap-2 text-blue-400 mb-2"><Clock size={14} /><span className="text-xs text-slate-400">Total Hours</span></div>
                        <p className="text-2xl font-bold text-white">{totalHours}<span className="text-sm text-slate-500 ml-1">hrs</span></p>
                    </div>
                    <div className="bg-slate-900 rounded-xl border border-white/5 p-4">
                        <div className="flex items-center gap-2 text-green-400 mb-2"><Users size={14} /><span className="text-xs text-slate-400">Active Today</span></div>
                        <p className="text-2xl font-bold text-white">{activeTodayCount}<span className="text-sm text-slate-500 ml-1">staff</span></p>
                    </div>
                    <div className="bg-slate-900 rounded-xl border border-white/5 p-4">
                        <div className="flex items-center gap-2 text-purple-400 mb-2"><TrendingUp size={14} /><span className="text-xs text-slate-400">Avg per Person</span></div>
                        <p className="text-2xl font-bold text-white">{avgHoursPerPerson}<span className="text-sm text-slate-500 ml-1">hrs</span></p>
                    </div>
                    <div className="bg-slate-900 rounded-xl border border-white/5 p-4">
                        <div className="flex items-center gap-2 text-amber-400 mb-2"><Calendar size={14} /><span className="text-xs text-slate-400">Sessions</span></div>
                        <p className="text-2xl font-bold text-white">{filteredSessions.length}</p>
                    </div>
                </div>

                {/* Staff filter */}
                <div className="flex items-center gap-3">
                    <select
                        value={staffFilter}
                        onChange={e => { setStaffFilter(e.target.value); setExpandedStaff(null) }}
                        className="bg-slate-900 border border-white/5 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500"
                    >
                        <option value="">All Staff</option>
                        {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                    </select>
                </div>

                {/* Per-staff hours breakdown */}
                {isLoading ? (
                    <div className="space-y-3">
                        {Array(4).fill(0).map((_, i) => (
                            <div key={i} className="bg-slate-900 rounded-xl border border-white/5 p-4 animate-pulse h-20" />
                        ))}
                    </div>
                ) : staffSummary.length === 0 ? (
                    <div className="bg-slate-900 rounded-xl border border-white/5 p-12 text-center text-slate-500">
                        No sessions found for this period
                    </div>
                ) : (
                    <div className="space-y-2">
                        {staffSummary.map(staff => {
                            const pct = durationBar(staff.totalMins, maxMins)
                            const isExpanded = expandedStaff === staff.staff_id
                            const user = users.find(u => u.id === staff.staff_id)
                            const colors = ['bg-blue-600', 'bg-purple-600', 'bg-green-600', 'bg-amber-600', 'bg-rose-600']
                            const colorIdx = staff.staff_name ? staff.staff_name.charCodeAt(0) % colors.length : 0

                            return (
                                <div key={staff.staff_id} className="bg-slate-900 rounded-xl border border-white/5 overflow-hidden">
                                    {/* Staff row */}
                                    <button
                                        onClick={() => setExpandedStaff(isExpanded ? null : staff.staff_id)}
                                        className="w-full flex items-center gap-4 p-4 hover:bg-white/2 transition text-left"
                                    >
                                        {/* Avatar */}
                                        <div className="shrink-0">
                                            {user?.profile_photo_url ? (
                                                <div className="w-10 h-10 rounded-full overflow-hidden bg-slate-800">
                                                    <img src={user.profile_photo_url} alt={staff.staff_name} className="w-full h-full object-cover object-top" />
                                                </div>
                                            ) : (
                                                <div className={`w-10 h-10 rounded-full ${colors[colorIdx]} flex items-center justify-center text-sm font-bold text-white`}>
                                                    {staff.staff_name?.[0]?.toUpperCase() || '?'}
                                                </div>
                                            )}
                                        </div>

                                        {/* Name + meta */}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center justify-between gap-3 mb-1.5">
                                                <div className="flex items-center gap-2 min-w-0">
                                                    <span className="font-medium text-white truncate">{staff.staff_name}</span>
                                                    {staff.lastSeen === today && (
                                                        <span className="text-[10px] bg-green-500/15 text-green-400 px-1.5 py-0.5 rounded font-medium shrink-0">Active today</span>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-4 shrink-0 text-sm">
                                                    <span className="font-bold text-white">{formatDuration(staff.totalMins)}</span>
                                                    <span className="text-slate-500 text-xs">{staff.daysPresent}d · {staff.sessionCount} sessions</span>
                                                    {isExpanded ? <ChevronUp size={14} className="text-slate-400" /> : <ChevronDown size={14} className="text-slate-400" />}
                                                </div>
                                            </div>
                                            {/* Progress bar */}
                                            <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                                                <div
                                                    className={`h-full rounded-full transition-all ${colors[colorIdx]}`}
                                                    style={{ width: `${pct}%` }}
                                                />
                                            </div>
                                        </div>
                                    </button>

                                    {/* Expanded: weekly chart + session list */}
                                    {isExpanded && (
                                        <div className="border-t border-white/5 p-4 space-y-4">
                                            {/* 7-day bar chart */}
                                            <div>
                                                <p className="text-xs text-slate-400 mb-3">Last 7 days</p>
                                                <div className="flex items-end gap-1.5 h-20">
                                                    {weeklyData.map(day => (
                                                        <div key={day.date} className="flex-1 flex flex-col items-center gap-1">
                                                            <span className="text-[9px] text-slate-500">{day.hours > 0 ? `${day.hours}h` : ''}</span>
                                                            <div className="w-full bg-slate-800 rounded-t-sm overflow-hidden" style={{ height: '48px' }}>
                                                                <div
                                                                    className={`w-full rounded-t-sm transition-all ${day.date === today ? 'bg-blue-500' : `${colors[colorIdx]}/60`}`}
                                                                    style={{ height: `${Math.round((day.mins / maxWeeklyMins) * 48)}px`, marginTop: `${48 - Math.round((day.mins / maxWeeklyMins) * 48)}px` }}
                                                                />
                                                            </div>
                                                            <span className={`text-[9px] font-medium ${day.date === today ? 'text-blue-400' : 'text-slate-500'}`}>{day.label}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>

                                            {/* Recent sessions */}
                                            <div>
                                                <p className="text-xs text-slate-400 mb-2">Recent Sessions</p>
                                                <div className="space-y-1.5 max-h-48 overflow-y-auto">
                                                    {staff.sessions
                                                        .sort((a, b) => b.session_date.localeCompare(a.session_date))
                                                        .slice(0, 10)
                                                        .map((s, i) => (
                                                            <div key={i} className="flex items-center gap-3 bg-slate-800/50 rounded-lg px-3 py-2 text-xs">
                                                                <span className="text-slate-400 w-24 shrink-0">
                                                                    {new Date(s.session_date + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                                                                </span>
                                                                <span className="text-green-400">{formatTime(s.start_time)}</span>
                                                                <span className="text-slate-600">→</span>
                                                                <span className={s.end_time ? 'text-red-400' : 'text-amber-400'}>
                                                                    {s.end_time ? formatTime(s.end_time) : 'Active'}
                                                                </span>
                                                                <span className="flex-1 text-right font-medium text-white">{formatDuration(s.duration_minutes)}</span>
                                                            </div>
                                                        ))}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )
                        })}
                    </div>
                )}
            </div>
        </StaffLayout>
    )
}
